import { useMutation, useQuery } from "@apollo/client";
import {
  ActionIcon,
  Button,
  Grid,
  Group,
  LoadingOverlay,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import { IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { UPDATE_CATEGORY } from "apollo/mutuations";
import { GET_CATEGORY } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useRef, useState } from "react";
import { Trash } from "tabler-icons-react";

const CategoryEditModal = ({ setOpenedEdit, editId }) => {
  const [updateCategory] = useMutation(UPDATE_CATEGORY);
  const form = useForm({
    initialValues: {
      childrens: {
        create: [],
        update: [],
        delete: [],
      },
    },
  });

  const { loading } = useQuery(GET_CATEGORY, {
    variables: { id: editId },
    onCompleted(data) {
      const newUpdateArr = data.category.children.map(({ name, ...rest }) => ({
        name,
        ...rest,
      }));
      form.setValues({
        id: editId,
        name: data.category.name,

        image: data.category.imageUrl,
        childrens: {
          create: [],
          update: [...newUpdateArr],
          delete: [],
        },
      });
    },
  });

  const [subCategoryFiles, setSubCategoryFiles] = useState({});
  const [mainCategoryFile, setMainCategoryFile] = useState(null);
  const { height } = useViewportSize();
  const mainFileInputRef = useRef(null);

  const handleMainFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setMainCategoryFile(files[0]);
      form.setFieldValue("image", files[0]);
    }
  };
  const handleSubCategoryImageUpload = (files, type, index) => {
    if (files.length > 0) {
      const file = files[0];
      const key = `${type}-${index}`;
      setSubCategoryFiles((prev) => ({ ...prev, [key]: file }));
      form.setFieldValue(`childrens.${type}.${index}.image`, file);
    }
  };

  const handleFields = () => {
    return (
      <>
        {form.values.childrens?.update?.map((item, index) => (
          <Grid key={`update-${index}`}>
            <Grid.Col span={3}>
              <TextInput
                placeholder="Sub Category"
                required
                label="Sub Category"
                {...form.getInputProps(`childrens.update.${index}.name`)}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Button
                  onClick={() => {
                    document
                      .getElementById(`file-input-update-${index}`)
                      .click();
                  }}
                  variant="outline"
                  leftIcon={<IconUpload size={16} />}
                  //   style={{ marginTop: "20px" }}
                >
                  Upload Image
                </Button>
                <input
                  id={`file-input-update-${index}`}
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    handleSubCategoryImageUpload(
                      e.target.files,
                      "update",
                      index,
                    )
                  }
                />
                {/* Updated image display with type-specific key */}
                {subCategoryFiles[`update-${index}`] ? (
                  <img
                    src={URL.createObjectURL(
                      subCategoryFiles[`update-${index}`],
                    )}
                    width="130"
                    alt="Sub Category"
                  />
                ) : (
                  item.imageUrl && (
                    <img src={item.imageUrl} width="130" alt="Sub Category" />
                  )
                )}
                <ActionIcon
                  color="#ed522f"
                  onClick={() => {
                    form.removeListItem("childrens.update", index);
                    form.insertListItem("childrens.delete", parseInt(item.id));
                  }}
                  style={{
                    //   marginTop: "25px",
                    padding: "2px",
                    color: "red",
                  }}
                >
                  <Trash size={24} />
                </ActionIcon>
              </div>
            </Grid.Col>
          </Grid>
        ))}

        {form.values.childrens?.create?.map((item, index) => (
          <Grid key={`create-${index}`}>
            <Grid.Col span={3}>
              <TextInput
                placeholder="New Sub Category"
                required
                label="New Sub Category"
                {...form.getInputProps(`childrens.create.${index}.name`)}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Button
                onClick={() => {
                  document.getElementById(`file-input-create-${index}`).click();
                }}
                variant="outline"
                color="blue"
                style={{ marginTop: "25px" }}
              >
                Upload Image
              </Button>
              <input
                id={`file-input-create-${index}`}
                type="file"
                accept={IMAGE_MIME_TYPE}
                style={{ display: "none" }}
                onChange={(e) =>
                  handleSubCategoryImageUpload(e.target.files, "create", index)
                }
              />
              {/* Updated image display with type-specific key */}
              {subCategoryFiles[`create-${index}`] ? (
                <img
                  src={URL.createObjectURL(subCategoryFiles[`create-${index}`])}
                  width="130"
                  alt="New Sub Category"
                />
              ) : null}
            </Grid.Col>
          </Grid>
        ))}
      </>
    );
  };
  const submit = () => {
    const { id, name, childrens } = form.values;
    const cleanChildrenUpdate = childrens.update.map(
      ({ __typename, ...child }, index) => {
        const { name } = child;
        const fileKey = `update-${index}`;
        return {
          id: child.id,
          name: name,
          ...(subCategoryFiles[fileKey] && {
            image: subCategoryFiles[fileKey],
          }),
        };
      },
    );

    const childrenCreate = childrens.create.map(({ name }, index) => ({
      name: name,
      image: subCategoryFiles[`create-${index}`] || null,
    }));

    const variables = {
      id,
      name: name,
      children: {
        create: childrenCreate,
        update: cleanChildrenUpdate,
        delete: childrens.delete,
      },
    };
    if (mainCategoryFile) {
      variables.image = form.values.image;
    }
    console.log(variables);
    updateCategory({
      variables,
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Category Edited Successfully",
        });
        form.reset();
        setOpenedEdit(false);
      },
      onError(err) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            err?.graphQLErrors?.[0]?.message || "Failed to update Category ",
        });
      },
    });
  };

  return (
    <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <form onSubmit={form.onSubmit(() => submit())} noValidate>
        <Grid>
          <Grid.Col span={3}>
            <TextInput
              required
              label="Category Name "
              placeholder="Enter category name"
              {...form.getInputProps("name")}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <Button
              onClick={() => mainFileInputRef.current.click()} // Use the main file input ref
              variant="outline"
              leftIcon={<IconUpload size={16} />}
              style={{ marginTop: "25px" }}
            >
              Upload Image
            </Button>
            <input
              type="file"
              accept={IMAGE_MIME_TYPE}
              ref={mainFileInputRef}
              style={{ display: "none" }}
              onChange={handleMainFileChange} // Use main file handler
            />
            <span style={{ marginLeft: "15px" }}>
              {mainCategoryFile ? (
                <img
                  src={URL.createObjectURL(mainCategoryFile)}
                  width="130"
                  alt="Main Category"
                />
              ) : (
                <img src={form.values.image} width="130" alt="Main Category" />
              )}
            </span>
          </Grid.Col>
          <Grid.Col span={12}>
            {handleFields()}
            <Group position="start" mt="md">
              <Button
                color="blue"
                variant="outline"
                fullWidth
                style={{ width: "200px" }}
                onClick={() => {
                  form.insertListItem("childrens.create", {
                    name: "",
                    image: null,
                  });
                  setSubCategoryFiles((prev) => ({
                    ...prev,
                    [form.values.childrens.create.length]: null,
                  }));
                }}
              >
                Add new subcategory
              </Button>
            </Group>
          </Grid.Col>
          <Grid.Col span={4}>
            <Button
              style={{
                width: "25%",
                marginTop: "15px",
                backgroundColor: "#FF6A00",
                color: "#FFFFFF",
              }}
              type="submit"
              color="blue"
              fullWidth
            >
              Submit
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </ScrollArea>
  );
};

export default CategoryEditModal;
